import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';
import { PageHeader } from './layout/PageHeader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { ChartCard, CHART_COLORS } from './charts/ChartCard';
import { NexusDonut } from './charts/NexusCharts';
import { PageLoader } from './PageLoader';
import { tokens } from '../theme/tokens';
import { formatCurrencyShort } from '../utils/finance';
import { strategicCategoriesApi } from '../api/ppm';

const RECENT_COLORS_KEY = 'strategicAlignment.recentColors.v1';
// Match Roadmaps recommended palette.
const DEFAULT_COLORS = ['#2563EB', '#1D4ED8', '#60A5FA', '#0B1220', '#94A3B8', '#06B6D4', '#EF4444', '#22C55E'];

function plural(n: number, one: string, many?: string) {
  const v = Number(n || 0);
  return `${v} ${v === 1 ? one : many || `${one}s`}`;
}

function normalizeHexColor(v: string) {
  const s = String(v || '').trim();
  if (!s) return '';
  const withHash = s.startsWith('#') ? s : `#${s}`;
  if (/^#[0-9A-Fa-f]{6}$/.test(withHash)) return withHash.toUpperCase();
  return s;
}

function readRecentColors(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_COLORS_KEY);
    const arr = raw ? (JSON.parse(raw) as any) : [];
    const cleaned = (Array.isArray(arr) ? arr : []).map((x) => normalizeHexColor(String(x))).filter((x) => /^#[0-9A-F]{6}$/.test(x));
    return Array.from(new Set(cleaned)).slice(0, 10);
  } catch {
    return [];
  }
}

function writeRecentColors(colors: string[]) {
  try {
    localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(colors.slice(0, 10)));
  } catch {
    // ignore
  }
}

export const StrategicAlignmentPage: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [panel, setPanel] = useState<null | 'alignments' | 'portfolios' | 'projects' | 'budget'>(null);
  const [recentColors, setRecentColors] = useState<string[]>(() => readRecentColors());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<{ name: string; description: string; color: string; startDate: string; endDate: string }>({
    name: '',
    description: '',
    color: '#3B82F6',
    startDate: '',
    endDate: '',
  });

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await strategicCategoriesApi.portfolioSummary();
      setData(res.data || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load strategic alignment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const openId = new URLSearchParams(location.search || '').get('openId');
    if (!openId) return;
    setExpandedIds((prev) => {
      const next = new Set(Array.from(prev));
      next.add(String(openId));
      return next;
    });
    // Scroll into view (after the list renders).
    requestAnimationFrame(() => {
      const el = document.getElementById(`alignment-${String(openId)}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.search]);

  const rows = useMemo(() => (Array.isArray(data?.rows) ? data.rows : []), [data]);

  const pie = useMemo(() => {
    // Model after the reference chart: use budgeted cost style numbers (we use portfolioBudget under each alignment).
    return rows
      .map((r: any, idx: number) => ({
        name: String(r.name || '—'),
        value: Number(r.portfolioBudget || 0),
        portfoliosCount: Number(r.portfoliosCount || 0),
        projectsCount: Number(r.projectsCount || 0),
        color: normalizeHexColor(String(r.color || '')) || CHART_COLORS[idx % CHART_COLORS.length],
      }))
      .filter((r: any) => r.value > 0);
  }, [rows]);

  const money = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }), []);
  const moneyCompact = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }),
    [],
  );
  const distinctPortfolios = useMemo(() => {
    const m = new Map<string, any>();
    (rows || []).forEach((r: any) => {
      (r.portfolios || []).forEach((p: any) => {
        if (p?.id) m.set(String(p.id), p);
      });
    });
    return Array.from(m.values()).sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [rows]);

  const distinctProjects = useMemo(() => {
    // Show ALL projects listed under each alignment (duplicates across alignments are expected/allowed).
    const out: any[] = [];
    (rows || []).forEach((r: any) => {
      const arr = Array.isArray(r.projects) ? r.projects : [];
      arr.forEach((p: any) => out.push({ ...p, __alignmentId: r.id, __alignmentName: r.name }));
    });
    return out;
  }, [rows]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', description: '', color: '#3B82F6', startDate: '', endDate: '' });
    setEditOpen(true);
  };

  const openEdit = (r: any) => {
    setEditingId(String(r.id));
    setForm({
      name: String(r.name || ''),
      description: String(r.description || ''),
      color: String(r.color || '#3B82F6'),
      startDate: r.startDate ? String(r.startDate).slice(0, 10) : '',
      endDate: r.endDate ? String(r.endDate).slice(0, 10) : '',
    });
    setEditOpen(true);
  };

  const save = async () => {
    try {
      const normalizedColor = normalizeHexColor(form.color || '');
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() ? form.description.trim() : null,
        color: normalizedColor || null,
        startDate: form.startDate.trim() ? new Date(`${form.startDate}T00:00:00.000Z`).toISOString() : null,
        endDate: form.endDate.trim() ? new Date(`${form.endDate}T00:00:00.000Z`).toISOString() : null,
      };
      if (!payload.name) {
        setError('Name is required');
        return;
      }
      if (editingId) await strategicCategoriesApi.update(editingId, payload);
      else await strategicCategoriesApi.create(payload);
      if (payload.color && /^#[0-9A-F]{6}$/.test(payload.color)) {
        const next = [payload.color, ...recentColors.filter((c) => c !== payload.color)].slice(0, 10);
        setRecentColors(next);
        writeRecentColors(next);
      }
      setEditOpen(false);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Save failed');
    }
  };

  const remove = async (id: string) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Delete this alignment?');
    if (!ok) return;
    try {
      await strategicCategoriesApi.remove(id);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pb: 2 }}>
      <PageHeader
        title="Strategic Alignment"
        subtitle="Trace strategy → portfolios → projects. Keeps work intentional and prevents busy work."
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" variant="outlined" onClick={refresh} sx={{ borderRadius: 2 }}>
              Refresh
            </Button>
            <Button size="small" variant="contained" onClick={openCreate} sx={{ borderRadius: 2 }}>
              Add alignment
            </Button>
          </Stack>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && data ? (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Alignments"
                value={Number(data.totalCategories || 0)}
                subtitle="Strategic themes"
                accent={tokens.color.accent}
                onClick={() => setPanel((p) => (p === 'alignments' ? null : 'alignments'))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Portfolios"
                value={Number(data.totalDistinctPortfolios || 0)}
                subtitle="Distinct portfolios mapped"
                accent={tokens.color.success}
                onClick={() => setPanel((p) => (p === 'portfolios' ? null : 'portfolios'))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Projects mapped"
                value={Number(data.totalDistinctProjects || 0)}
                subtitle={`${Number(data.totalProjectsMapped || 0)} total listings (duplicates allowed)`}
                accent={tokens.color.accent}
                onClick={() => setPanel((p) => (p === 'projects' ? null : 'projects'))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title="Portfolio budget"
                value={money.format(Number(data.totalDistinctPortfolioBudget || 0))}
                subtitle="Distinct sum (no double counting)"
                accent={tokens.color.warning}
                onClick={() => setPanel((p) => (p === 'budget' ? null : 'budget'))}
              />
            </Grid>
          </Grid>

          <Collapse in={!!panel} unmountOnExit>
            <TableCard
              title={
                panel === 'alignments'
                  ? 'Alignments (manage)'
                  : panel === 'portfolios'
                    ? 'Portfolios (mapped)'
                    : panel === 'projects'
                      ? 'Projects (mapped)'
                      : 'Portfolio budget (mapped)'
              }
            >
              {panel === 'alignments' ? (
                <Stack spacing={1}>
                  {rows.map((r: any) => (
                    <Box key={r.id} sx={{ p: 1, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.color.border}` }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                          <Chip size="small" variant="outlined" label={r.name} sx={{ fontWeight: 700, height: 22 }} />
                          <Chip size="small" variant="outlined" label={`${Number(r.portfoliosCount || 0)} portfolios`} sx={{ height: 22, fontWeight: 650 }} />
                          <Chip size="small" variant="outlined" label={`${Number(r.projectsCount || 0)} projects`} sx={{ height: 22, fontWeight: 650 }} />
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="outlined" onClick={() => openEdit(r)} sx={{ borderRadius: 2 }}>
                            Edit
                          </Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => remove(String(r.id))} sx={{ borderRadius: 2 }}>
                            Delete
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : null}

              {panel === 'portfolios' ? (
                <Stack spacing={1}>
                  {distinctPortfolios.map((p: any) => (
                    <Box key={p.id} sx={{ p: 1, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.color.border}` }}>
                      <Typography sx={{ fontWeight: 700 }}>{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Budget {money.format(Number(p.totalBudget || 0))}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : null}

              {panel === 'projects' ? (
                distinctProjects.length ? (
                  <Stack spacing={1}>
                    {rows.map((r: any) => (
                      <Box key={r.id} sx={{ p: 1.25, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.color.border}` }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                          <Typography sx={{ fontWeight: 700 }}>{r.name}</Typography>
                          <Chip size="small" variant="outlined" label={`${Number(r.projectsCount || 0)} projects`} sx={{ height: 22, fontWeight: 650 }} />
                        </Stack>
                        {Array.isArray(r.projects) && r.projects.length ? (
                          <Stack spacing={0.75} sx={{ mt: 1 }}>
                            {r.projects.map((p: any) => (
                              <Box key={`${r.id}-${p.id}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 650, minWidth: 0 }} noWrap title={p.name || p.id}>
                                  {p.name || p.id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                  {money.format(Number(p.budgetedCost || 0))}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No projects mapped.
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info" variant="outlined">
                    No projects mapped yet.
                  </Alert>
                )
              ) : null}

              {panel === 'budget' ? (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Total portfolio budget across distinct mapped portfolios: <b>{money.format(Number(data.totalDistinctPortfolioBudget || 0))}</b>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total allocated-to-projects across those portfolios: <b>{money.format(Number(data.totalDistinctAllocatedBudget || 0))}</b>
                  </Typography>
                </Stack>
              ) : null}
            </TableCard>
          </Collapse>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ChartCard title="Strategic Alignment" subtitle="Portfolio budget by alignment" height={380}>
                {pie.length ? (
                  <NexusDonut
                    data={pie.map((slice: any) => ({ name: slice.name, value: Number(slice.value || 0), color: slice.color }))}
                    valueFormatter={(v) => formatCurrencyShort(v)}
                    height={300}
                    centerLabel="Total"
                    centerValue={formatCurrencyShort(pie.reduce((s: number, slice: any) => s + Number(slice.value || 0), 0))}
                  />
                ) : (
                  <Alert severity="info" variant="outlined">
                    No portfolio → alignment mappings yet.
                  </Alert>
                )}
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <TableCard
                title="Alignments"
                actions={
                  <Button size="small" variant="outlined" onClick={() => setPanel((p) => (p === 'alignments' ? null : 'alignments'))} sx={{ borderRadius: 2 }}>
                    Manage
                  </Button>
                }
              >
                <Stack spacing={1}>
                  {(rows || []).map((r: any, idx: number) => {
                    const color = normalizeHexColor(String(r.color || '')) || CHART_COLORS[idx % CHART_COLORS.length];
                    const portfolios = Array.isArray(r.portfolios) ? r.portfolios : [];
                    const projects = Array.isArray(r.projects) ? r.projects : [];
                    const start = r.startDate ? String(r.startDate).slice(0, 10) : '';
                    const end = r.endDate ? String(r.endDate).slice(0, 10) : '';
                    const dateLabel = start || end ? `${start || '—'} → ${end || '—'}` : '';

                    return (
                      <Accordion
                        key={r.id || idx}
                        id={`alignment-${String(r.id || idx)}`}
                        disableGutters
                        elevation={0}
                        expanded={expandedIds.has(String(r.id))}
                        onChange={(_, isExpanded) => {
                          const id = String(r.id);
                          setExpandedIds((prev) => {
                            const next = new Set(Array.from(prev));
                            if (isExpanded) next.add(id);
                            else next.delete(id);
                            return next;
                          });
                        }}
                        sx={{
                          border: `1px solid ${tokens.color.border}`,
                          borderRadius: `${tokens.radius.md}px`,
                          '&:before': { display: 'none' },
                          overflow: 'hidden',
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, py: 0.5 }}>
                          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: 999, backgroundColor: color }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontWeight: 700 }} noWrap title={r.name}>
                                {r.name}
                              </Typography>
                              {dateLabel ? (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {dateLabel}
                                </Typography>
                              ) : null}
                            </Box>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <Chip size="small" variant="outlined" label={plural(Number(r.portfoliosCount || 0), 'portfolio')} sx={{ height: 22, fontWeight: 650 }} />
                              <Chip size="small" variant="outlined" label={plural(Number(r.projectsCount || 0), 'project')} sx={{ height: 22, fontWeight: 650 }} />
                              <Chip size="small" variant="outlined" label={`${moneyCompact.format(Number(r.portfolioBudget || 0))} budget`} sx={{ height: 22, fontWeight: 650 }} />
                            </Stack>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, pb: 1.5 }}>
                          {r.description ? (
                            <Alert severity="info" variant="outlined" sx={{ mb: 1.25 }}>
                              {r.description}
                            </Alert>
                          ) : null}

                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: tokens.color.textSecondary, mb: 0.75 }}
                              >
                                Portfolios
                              </Typography>
                              {portfolios.length ? (
                                <Stack spacing={0.75}>
                                  {portfolios.map((p: any) => (
                                    <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 650, minWidth: 0 }} noWrap title={p.name}>
                                        {p.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        {moneyCompact.format(Number(p.totalBudget || 0))}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No portfolios mapped.
                                </Typography>
                              )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: tokens.color.textSecondary, mb: 0.75 }}
                              >
                                Projects
                              </Typography>
                              {projects.length ? (
                                <Stack spacing={0.75}>
                                  {projects.map((p: any) => (
                                    <Box key={`${r.id}-${p.id}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap title={p.name || p.id}>
                                          {p.name || p.id}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                          {p.status ? String(p.status) : '—'}
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        {moneyCompact.format(Number(p.budgetedCost || 0))}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No projects mapped.
                                </Typography>
                              )}
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 1.25 }} />
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" variant="outlined" onClick={() => openEdit(r)} sx={{ borderRadius: 2 }}>
                              Edit
                            </Button>
                            <Button size="small" color="error" variant="outlined" onClick={() => remove(String(r.id))} sx={{ borderRadius: 2 }}>
                              Delete
                            </Button>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              </TableCard>
            </Grid>
          </Grid>
        </>
      ) : null}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit alignment' : 'Add alignment'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
          <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
          <Divider />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField label="Start date" type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="End date" type="date" value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>
          <TextField label="Color" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} fullWidth />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: 999,
                backgroundColor: normalizeHexColor(form.color || '') || tokens.color.border,
                border: '1px solid rgba(17,24,39,0.15)',
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>
              Recommended:
            </Typography>
            {DEFAULT_COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => setForm((v) => ({ ...v, color: c }))}
                title={c}
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  backgroundColor: c,
                  border: '1px solid rgba(17,24,39,0.15)',
                  cursor: 'pointer',
                }}
              />
            ))}
            {recentColors.length ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650, ml: 1 }}>
                  Recent:
                </Typography>
                {recentColors.slice(0, 6).map((c) => (
                  <Box
                    key={c}
                    onClick={() => setForm((v) => ({ ...v, color: c }))}
                    title={c}
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      backgroundColor: c,
                      border: '1px solid rgba(17,24,39,0.15)',
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </PageLoader>
  );
};

export default StrategicAlignmentPage;


